import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AddOutlined, MoreVertOutlined,
  DriveFileRenameOutlineOutlined, ContentCopyOutlined, DeleteOutlineOutlined,
  ArticleOutlined, CircleOutlined,
} from '@mui/icons-material';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider, CircularProgress } from '@mui/material';
import { FIELD_COLOR, FORM_TYPE_COLORS, STATUS_DOT } from './constants';

export default function FormTabs({ forms, activeFormId, onSwitch, onAdd, onRename, onDuplicate, onDelete, loading }) {
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal,  setRenameVal]  = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuFormId, setMenuFormId] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { if (renamingId && inputRef.current) inputRef.current.select(); }, [renamingId]);

  const startRename  = (form) => { setMenuAnchor(null); setRenamingId(form._id); setRenameVal(form.name); };
  const commitRename = () => { if (renamingId && renameVal.trim()) onRename(renamingId, renameVal.trim()); setRenamingId(null); };

  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-end',
      px: 2, pt: '8px',
      borderTop: '1px solid #e8edf2',
      background: '#f5f7fa', overflowX: 'auto', flexShrink: 0,
      '&::-webkit-scrollbar': { height: 3 },
      '&::-webkit-scrollbar-thumb': { background: '#d4dbe6', borderRadius: 2 },
    }}>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, pb: 1 }}>
          <CircularProgress size={14} sx={{ color: FIELD_COLOR }} />
          <Typography sx={{ fontSize: '0.72rem', color: '#b0bac8' }}>Loading forms…</Typography>
        </Box>
      )}

      {!loading && forms.length === 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, pb: 1 }}>
          <ArticleOutlined sx={{ fontSize: 16, color: '#d4dbe6' }} />
          <Typography sx={{ fontSize: '0.72rem', color: '#b0bac8' }}>No forms yet — create one</Typography>
        </Box>
      )}

      {!loading && forms.map((form) => {
        const isActive    = form._id === activeFormId;
        const typeColor   = FORM_TYPE_COLORS[form.formType] || FIELD_COLOR;
        const statusColor = STATUS_DOT[form.status] || '#94a3b8';

        return (
          <Box
            key={form._id}
            onClick={() => { if (!renamingId) onSwitch(form._id); }}
            onDoubleClick={() => startRename(form)}
            sx={{
              position: 'relative', flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: 0.5,
              px: 1.5, height: 34, cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              border: isActive ? '1px solid #e8edf2' : '1px solid transparent',
              borderBottom: isActive ? '1px solid #f5f7fa' : '1px solid transparent',
              background: isActive ? '#fff' : 'transparent',
              mb: '-1px', transition: 'all 0.12s',
              '&:hover': { background: isActive ? '#fff' : 'rgba(0,0,0,0.04)' },
              '&:hover .tab-ctx': { opacity: 1 },
            }}
          >
            {/* Status dot */}
            <Box sx={{
              flexShrink: 0, width: 6, height: 6, borderRadius: '50%',
              background: statusColor,
              boxShadow: form.status === 'published' ? `0 0 0 2px ${alpha(statusColor, 0.25)}` : 'none',
            }} />

            {/* Name / inline rename */}
            {renamingId === form._id ? (
              <Box
                component="input"
                ref={inputRef}
                value={renameVal}
                onChange={e => setRenameVal(e.target.value)}
                onBlur={commitRename}
                onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingId(null); }}
                onClick={e => e.stopPropagation()}
                sx={{
                  border: 'none', outline: 'none', background: 'transparent',
                  fontSize: '0.78rem', fontWeight: 600, color: typeColor,
                  fontFamily: 'inherit', p: 0,
                  width: Math.max(60, renameVal.length * 8) + 'px',
                }}
              />
            ) : (
              <Typography sx={{
                fontSize: '0.78rem', fontWeight: isActive ? 600 : 400,
                color: isActive ? typeColor : '#7a8899',
                userSelect: 'none', whiteSpace: 'nowrap',
                maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {form.name}
              </Typography>
            )}

            {/* Context menu trigger */}
            <Box
              className="tab-ctx"
              onClick={e => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setMenuFormId(form._id); }}
              sx={{
                opacity: isActive ? 0.5 : 0, transition: 'opacity 0.12s',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 16, height: 16, borderRadius: '3px', flexShrink: 0,
                '&:hover': { background: 'rgba(0,0,0,0.08)', opacity: '1 !important' },
              }}
            >
              <MoreVertOutlined sx={{ fontSize: 12, color: '#5a6474' }} />
            </Box>
          </Box>
        );
      })}

      {/* Add button */}
      <Box
        onClick={onAdd}
        sx={{
          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 30, height: 30, mb: '2px', ml: 0.5,
          borderRadius: '6px', cursor: 'pointer', color: '#9ea8b3', transition: 'all 0.12s',
          '&:hover': { background: alpha(FIELD_COLOR, 0.08), color: FIELD_COLOR },
        }}
      >
        <AddOutlined sx={{ fontSize: 16 }} />
      </Box>

      {/* Context menu */}
      <Menu
        anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
        PaperProps={{ sx: { mt: 0.5, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', borderRadius: 2, minWidth: 160 } }}
      >
        <MenuItem onClick={() => { startRename(forms.find(f => f._id === menuFormId)); }} sx={{ fontSize: '0.82rem' }}>
          <ListItemIcon><DriveFileRenameOutlineOutlined sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { onDuplicate(menuFormId); setMenuAnchor(null); }} sx={{ fontSize: '0.82rem' }}>
          <ListItemIcon><ContentCopyOutlined sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { onDelete(menuFormId); setMenuAnchor(null); }} sx={{ fontSize: '0.82rem', color: '#dc2626' }}>
          <ListItemIcon><DeleteOutlineOutlined sx={{ fontSize: 16, color: '#dc2626' }} /></ListItemIcon>
          <ListItemText>Delete form</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}